import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Heart, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { celebrate } from "@/lib/celebration";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [donation, setDonation] = useState(null);
  const celebrated = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("unknown");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        if (data.status === "completed") {
          setDonation(data);
          setStatus("paid");
          if (!celebrated.current) {
            celebrated.current = true;
            celebrate();
          }
          return;
        }
      } catch {
        // keep polling
      }
      if (++tries < 15) {
        timer = setTimeout(poll, 2000);
      } else {
        setStatus("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <main data-testid="payment-success-page" className="relative min-h-screen bg-[#05030A] flex items-center justify-center px-6 overflow-hidden">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 55% 45% at 50% 40%, rgba(139,92,246,0.2), transparent 70%)" }} />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 text-center max-w-md"
      >
        {status === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-[#8B5CF6] mx-auto animate-spin" />
            <h1 className="mt-6 font-display text-4xl tracking-tight text-white">Confirming your donation…</h1>
            <p className="mt-3 text-slate-400 text-sm" data-testid="payment-status-text">Talking to Stripe — this takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-[#8B5CF6] mx-auto" strokeWidth={1.5} />
            <h1 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight text-white">
              You're a <span className="italic text-[#A78BFA]">legend</span>
            </h1>
            <p className="mt-4 text-slate-400 leading-relaxed" data-testid="payment-status-text">
              {donation ? (
                <>Thank you, <span className="text-white font-semibold">{donation.username}</span> — your <span className="text-[#A78BFA] font-semibold">${donation.total.toFixed(2)}</span> donation just filled the community goal bar.</>
              ) : (
                "Your donation was received."
              )}
            </p>
          </>
        )}
        {(status === "pending" || status === "unknown") && (
          <>
            <Heart className="w-14 h-14 text-[#8B5CF6] mx-auto fill-[#8B5CF6]/20" strokeWidth={1.5} />
            <h1 className="mt-6 font-display text-4xl tracking-tight text-white">Almost there</h1>
            <p className="mt-3 text-slate-400 text-sm" data-testid="payment-status-text">
              We couldn't confirm the payment yet — if you completed it, it will appear shortly.
            </p>
          </>
        )}
        <div className="mt-10 flex items-center justify-center gap-4">
          <Link
            to="/"
            data-testid="payment-success-home-link"
            className="px-8 py-3.5 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm font-bold tracking-[0.15em] transition-[background-color] duration-300"
          >
            BACK TO STORE
          </Link>
        </div>
      </motion.div>
    </main>
  );
}
